import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import MoodCalendar from "@/components/MoodCalendar";
import MedCalendar from "@/components/MedCalendar";
import MentalHealthTip from "@/components/MentalHealthTip";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

function parseLocalDate(dateStr) { const [y, m, d] = dateStr.split("-").map(Number); return new Date(y, m - 1, d); }

export default function Progress() {
  const { user } = useAuth();
  const [checkIns, setCheckIns] = useState([]);

  useEffect(() => { if (user) API.get("/daily-check-ins").then(({ data }) => setCheckIns(data || [])).catch(() => {}); }, [user]);

  const getWeeklySummary = () => {
    if (checkIns.length === 0) return null;
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const weekData = checkIns.filter(c => parseLocalDate(c.date) >= weekAgo);
    if (weekData.length === 0) return null;
    const avg = (key) => Math.round(weekData.reduce((s, c) => s + (c[key] || 0), 0) / weekData.length * 10) / 10;
    return { avgFocus: avg("focus_score"), avgMood: avg("mood_score"), avgMotivation: avg("motivation_score"), medAdherence: Math.round((weekData.filter(c => c.med_taken).length / weekData.length) * 100), daysLogged: weekData.length };
  };

  const getChartData = () => {
    const ago = new Date(); ago.setDate(ago.getDate() - 7);
    return checkIns.filter(c => parseLocalDate(c.date) >= ago).reverse().map(c => ({
      date: parseLocalDate(c.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      focus: c.focus_score || 0, mood: c.mood_score || 0, motivation: c.motivation_score || 0,
    }));
  };

  const summary = getWeeklySummary();
  const chartData = getChartData();

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="progress-page">
      <PageHeader title="Progress" subtitle="Track your weekly trends and insights" />
      {!summary ? <EmptyState icon={TrendingUp} title="No data yet" description="Log your first daily check-in to start seeing trends" action={<Button asChild className="rounded-full" data-testid="progress-start-checkin"><Link to="/">Start today's check-in</Link></Button>} /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-3">
            <SummaryCard label="Avg Focus" value={summary.avgFocus} icon="Focus" />
            <SummaryCard label="Avg Mood" value={summary.avgMood} icon="Mood" />
            <SummaryCard label="Med Adherence" value={`${summary.medAdherence}%`} icon="Med" />
          </div>
          <MentalHealthTip />
          {chartData.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4" data-testid="focus-chart">
              <h3 className="text-sm font-semibold mb-3">Focus Trend</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis domain={[0, 5]} tick={{ fontSize: 12 }} /><Tooltip /><Line type="monotone" dataKey="focus" stroke="hsl(var(--primary))" dot={{ r: 4 }} /></LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <MoodCalendar checkIns={checkIns} scoreKey="mood_score" title="Mood Calendar" />
          <MoodCalendar checkIns={checkIns} scoreKey="motivation_score" title="Motivation Calendar" />
          <MedCalendar checkIns={checkIns} />
          {summary.daysLogged >= 5 && <InsightCard title="You're tracking consistently" description={`Logged ${summary.daysLogged} days this week - great job!`} variant="success" />}
          {summary.medAdherence >= 80 && <InsightCard title="Strong medication adherence" description="You're staying on track with your medication routine." variant="success" />}
          {summary.medAdherence < 60 && summary.medAdherence > 0 && <InsightCard title="Medication adherence needs attention" description="Consider setting reminders or talking to your provider." variant="warning" />}
          <div className="flex gap-2">
            <Button className="flex-1" asChild><Link to="/messages">Message Provider</Link></Button>
            <Button className="flex-1" variant="outline" asChild><Link to="/appointments">Book Follow-Up</Link></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3 text-center" data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">{icon}</p>
      <p className="text-2xl font-bold text-primary">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function InsightCard({ title, description, variant = "default" }) {
  const bg = variant === "success" ? "bg-green-50" : variant === "warning" ? "bg-amber-50" : "bg-blue-50";
  const border = variant === "success" ? "border-green-200" : variant === "warning" ? "border-amber-200" : "border-blue-200";
  const text = variant === "success" ? "text-green-900" : variant === "warning" ? "text-amber-900" : "text-blue-900";
  return (
    <div className={`${bg} border ${border} rounded-lg p-3`}>
      <div className="flex gap-2"><AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${text}`} /><div><p className={`text-xs font-semibold ${text}`}>{title}</p><p className={`text-sm ${text} opacity-75 mt-1`}>{description}</p></div></div>
    </div>
  );
}
